"use client";

import { useEffect, useState } from "react";
import { Menu, Search, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        {[0, 1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-4 w-16 bg-muted/40 animate-pulse" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative inline-block bg-transparent px-0 py-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-foreground hover:bg-transparent focus:bg-transparent">
                <span className="relative">
                  {item.label}
                  <span className="pointer-events-none absolute -bottom-1 left-0 h-px w-full origin-left scale-x-0 bg-secondary transition-transform duration-300 ease-out group-hover:scale-x-100" aria-hidden="true" />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
const mobileSubCaptions: Record<string, string> = {
  home: "Return to the beginning",
  about: "Our legacy story",
  contact: "Get in touch"
};
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 px-6 py-8">
        {[0, 1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-12 w-full bg-muted/30 animate-pulse" />)}
      </div>;
  }
  return <nav className="flex flex-col px-6 pb-8 pt-4">
      {menuItems.map((item, i) => {
      const sub = mobileSubCaptions[item.label?.toLowerCase?.() || ""] || "";
      return <div key={item.id} data-index={i} className="border-b border-border/40 py-4">
            <SheetClose asChild>
              <Link to={item.href || "#"} onClick={onClose} className="group block">
                <div className="font-default text-3xl font-bold uppercase tracking-tight text-foreground transition-colors duration-200 group-hover:text-secondary sm:text-4xl">
                  {item.label}
                </div>
                {sub && <div className="mt-1 font-sans text-sm italic text-muted-foreground">
                    {sub}
                  </div>}
              </Link>
            </SheetClose>
          </div>;
    })}
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section data-section-id="4939" id="legacy-protection-header" className={`sticky top-0 z-50 w-full bg-background text-foreground transition-all duration-300 ${isScrolled ? "bg-background/90 backdrop-blur-lg border-b border-border" : "border-b border-border"}`}>
      <header className="mx-auto w-full max-w-[1400px] px-4 sm:px-6 lg:px-8">
        <div className="grid h-20 grid-cols-2 items-center md:grid-cols-3">
          <div className="flex items-center justify-start">
            <Link to="/" aria-label="Family Legacy Planning — Home" className="inline-flex items-center gap-2">
              <WvcLogo className="h-8 w-auto text-secondary" />
              <span className="font-default font-bold text-lg tracking-tight">FAMILY LEGACY PLANNING</span>
            </Link>
          </div>

          <div className="hidden items-center justify-center md:flex">
            <MenuProvider menu_id="16">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          <div className="flex items-center justify-end gap-2 sm:gap-3">
            <div className="hidden items-center gap-3 md:flex">
              <Separator orientation="vertical" className="h-6 bg-border/60" />

              <Button asChild variant="outline" size="sm" className="h-9 border border-secondary bg-transparent px-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-foreground transition-colors duration-200 hover:bg-secondary hover:text-background">
                <Link to="/HomePage" className="text-foreground">Get My Quote</Link>
              </Button>
            </div>

            <div className="flex items-center gap-1 md:hidden">
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Open menu" className="h-9 w-9 text-foreground hover:bg-transparent hover:text-secondary">
                    <Menu className="size-5" strokeWidth={1} />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-full max-w-full border-0 bg-background p-0 sm:max-w-full">
                  <SheetTitle className="sr-only">Navigation</SheetTitle>
                  <div className="flex h-full flex-col">
                    <div className="flex h-20 items-center justify-between border-b border-border px-6">
                      <Link to="/" onClick={() => setMobileOpen(false)} aria-label="Family Legacy Planning — Home">
                        <WvcLogo className="h-7 w-auto text-secondary" />
                      </Link>
                      <SheetClose asChild>
                        <Button variant="ghost" size="icon" aria-label="Close menu" className="h-9 w-9 text-foreground hover:bg-transparent hover:text-secondary">
                          <X className="size-5" strokeWidth={1} />
                        </Button>
                      </SheetClose>
                    </div>

                    <div className="flex-1 overflow-y-auto">
                      <MenuProvider menu_id="16">
                        <MobileMenuConsumer onClose={() => setMobileOpen(false)} />
                      </MenuProvider>

                      <div className="px-6 pb-10 pt-4">
                        <SheetClose asChild>
                          <Button asChild variant="outline" className="h-12 w-full border border-secondary bg-transparent text-[11px] font-semibold uppercase tracking-[0.25em] text-foreground hover:bg-secondary hover:text-background">
                            <Link to="/HomePage" className="text-foreground">Get My Quote</Link>
                          </Button>
                        </SheetClose>
                        <p className="mt-4 text-center font-sans text-xs italic text-muted-foreground">
                          Affordable final expense insurance solutions.
                        </p>
                      </div>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>
    </section>;
}